import * as React from "react"
import { useState, useEffect, useRef, useMemo } from "react"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "./command"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"
import { Button } from "./button"
import { cn } from "@/lib/utils"
import { Check, ChevronsUpDown, Loader2 } from "lucide-react"

/**
 * SearchableSelect - A type-ahead searchable dropdown
 * 
 * Supports two modes:
 * 1. API Search Mode: Pass `onSearch` for server-side filtering
 * 2. Static Mode: Pass `options` array for client-side filtering
 * 
 * Usage:
 * <SearchableSelect
 *   value={selectedValue}
 *   onValueChange={setSelectedValue}
 *   onSearch={async (query) => await searchAPI(query)}  // OR
 *   options={staticOptions}  // For client-side filtering
 *   placeholder="Type to search..."
 *   minChars={3}  // For API mode
 *   displayKey="name"
 *   valueKey="id"
 * />
 */
export function SearchableSelect({
  value,
  onValueChange,
  onSearch,
  options: initialOptions = [],
  placeholder = "Select...",
  searchPlaceholder,
  emptyMessage = "No results found.",
  minChars = 3,
  displayKey = "name",
  valueKey = "id",
  renderOption,
  className,
  disabled = false,
  allowClear = true,
  "data-testid": testId
}) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [options, setOptions] = useState(initialOptions)
  const [loading, setLoading] = useState(false)
  const [selectedLabel, setSelectedLabel] = useState("")
  const debounceRef = useRef(null)

  // Determine if using API search or static filtering
  const isApiMode = !!onSearch
  
  // Client-side filtered options (for static mode)
  const filteredStaticOptions = useMemo(() => {
    if (isApiMode) return []
    if (!searchQuery) return initialOptions
    return initialOptions.filter(opt => {
      const label = typeof displayKey === 'function' ? displayKey(opt) : opt[displayKey]
      return label?.toLowerCase().includes(searchQuery.toLowerCase())
    })
  }, [initialOptions, searchQuery, isApiMode, displayKey])

  // Get display value for selected item
  useEffect(() => {
    const searchIn = isApiMode ? options : initialOptions
    if (value && searchIn.length > 0) {
      const selected = searchIn.find(opt => opt[valueKey] === value)
      if (selected) {
        setSelectedLabel(typeof displayKey === 'function' ? displayKey(selected) : selected[displayKey])
      }
    } else if (!value) {
      setSelectedLabel("")
    }
  }, [value, options, initialOptions, displayKey, valueKey, isApiMode])

  // Handle API search with debounce
  useEffect(() => {
    if (!isApiMode) return

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    if (searchQuery.length >= minChars && onSearch) {
      setLoading(true)
      debounceRef.current = setTimeout(async () => {
        try {
          const results = await onSearch(searchQuery)
          setOptions(results || [])
        } catch (e) {
          console.error("Search failed:", e)
          setOptions([])
        } finally {
          setLoading(false)
        }
      }, 300)
    } else if (searchQuery.length < minChars) {
      setOptions([])
    }

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [searchQuery, minChars, onSearch, isApiMode])

  const handleSelect = (selectedValue) => {
    if (allowClear && selectedValue === value) {
      onValueChange("")
      setSelectedLabel("")
    } else {
      onValueChange(selectedValue)
      const searchIn = isApiMode ? options : initialOptions
      const selected = searchIn.find(opt => opt[valueKey] === selectedValue)
      if (selected) {
        setSelectedLabel(typeof displayKey === 'function' ? displayKey(selected) : selected[displayKey])
      }
    }
    setOpen(false)
    setSearchQuery("")
  }

  // Determine which options to display
  const displayOptions = isApiMode ? options : filteredStaticOptions

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between font-normal", className)}
          disabled={disabled}
          data-testid={testId}
        >
          {selectedLabel || placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder || (isApiMode ? `Type ${minChars}+ chars to search...` : "Search...")}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          {loading && (
            <div className="py-6 text-center">
              <Loader2 className="h-4 w-4 animate-spin mx-auto text-slate-400" />
              <p className="text-xs text-slate-500 mt-2">Searching...</p>
            </div>
          )}
          {!loading && isApiMode && searchQuery.length < minChars && (
            <div className="py-6 text-center text-sm text-slate-500">
              Type {minChars - searchQuery.length} more character{minChars - searchQuery.length > 1 ? 's' : ''} to search
            </div>
          )}
          {!loading && displayOptions.length === 0 && (isApiMode ? searchQuery.length >= minChars : searchQuery.length > 0) && (
            <CommandEmpty>{emptyMessage}</CommandEmpty>
          )}
          {!loading && displayOptions.length > 0 && (
            <CommandGroup className="max-h-60 overflow-y-auto">
              {displayOptions.map((option) => {
                const optValue = option[valueKey]
                const optLabel = typeof displayKey === 'function' ? displayKey(option) : option[displayKey]
                return (
                  <CommandItem
                    key={optValue}
                    value={optValue}
                    onSelect={() => handleSelect(optValue)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === optValue ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {renderOption ? renderOption(option) : optLabel}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          )}
          {!loading && !isApiMode && displayOptions.length === 0 && !searchQuery && initialOptions.length === 0 && (
            <div className="py-6 text-center text-sm text-slate-500">
              No options available
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default SearchableSelect
